import { useMemo, useState } from "react";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import EpisodeCard from "@/components/feature/EpisodeCard";
import { episodes } from "@/mocks/episodes";

const PAGE_SIZE = 8;
const statusOptions = ["全部", "已完结", "更新中"];

export default function EpisodesCategory() {
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [status, setStatus] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = episodes.filter((item) => status === "全部" || item.status === status);
    return list.sort((a, b) => (order === "asc" ? a.ep - b.ep : b.ep - a.ep));
  }, [order, status]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setOrder("desc");
    setStatus("全部");
    setPage(1);
  };

  return (
    <>
      <PageMeta
        title="年轻的母亲1剧集列表 - 年轻的母亲1官网"
        description="查看《年轻的母亲1》全部剧集更新和详情，轻松追剧。"
        keywords="年轻的母亲1剧集,年轻的母亲1分集剧情,年轻的母亲1在线观看"
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "剧集" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">剧集列表</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            本页展示《年轻的母亲1》各剧集的基本信息，包括集数、标题、更新状态与热度。点击任意卡片即可进入剧集详情页查看完整简介与剧情看点。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
        <div className="mb-7 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-filter-3-line text-primary-500" />
            <span>筛选与排序</span>
            <span className="rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-500">
              共 {filtered.length} 条
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setOrder("asc")}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  order === "asc" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                集数升序
              </button>
              <button
                type="button"
                onClick={() => setOrder("desc")}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  order === "desc" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                更新时间倒序
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    setStatus(option);
                    setPage(1);
                  }}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    status === option
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {paged.map((episode) => (
                <EpisodeCard key={episode.id} episode={episode} />
              ))}
            </div>
            <Pagination current={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            title="没有符合筛选条件的剧集"
            description="换个状态筛选试试，或者一键重置全部筛选条件。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        )}
      </div>
    </>
  );
}